(() => {
  try {
    // Track if the current PWS came from a show URL
    let isFromShowUrl = false;

    // Helper to clear all show-related storage
    const clearShowAttribution = () => {
      localStorage.removeItem("CURRENT_SHOW_ID");
      localStorage.removeItem("CURRENT_SHOW_SITE");
      localStorage.removeItem("CURRENT_SHOW_NAME");
      localStorage.removeItem("CURRENT_SHOW_HOSTESS_NAME");
    };

    // Validate show and check if it's closed - always calls API, no caching
    const validateShow = async (showId) => {
      if (!showId || !window.backend) return { isValid: false, partner: null, show: null };

      const [partner, show] = await Promise.all([
        window.backend.getPartnerByShowId(showId),
        window.backend.getShowById(showId),
      ]).catch((_) => [null, null]);

      console.log("VALIDATE SHOW ---> isClosed", partner, show);
      // If show is closed, clear show-specific attribution but preserve partner for redirect
      if (show?.isClosed) {
        clearShowAttribution();
        // Also clear stale check-in data for this show
        const checkInKey = `SHOW_CHECKED_IN_${showId}`;
        sessionStorage.removeItem(checkInKey);
        localStorage.removeItem(checkInKey);
        // Return partner data so caller can use siteName for PWS redirect
        return { isValid: false, partner, show };
      }

      if (partner?.siteName?.length) {
        return { isValid: true, partner, show };
      }

      return { isValid: false, partner: null, show: null };
    };

    const getCurrentPWS = async () => {
      const urlParams = new URLSearchParams(window.location.search);

      const fallbackPWS = sessionStorage.getItem("404_PATH")?.split("/")?.at(1);
      sessionStorage.removeItem("404_PATH");
      if (fallbackPWS?.length) {
        return fallbackPWS;
      }

      // Check for show ID from URL param or stored values
      const showIdFromUrl = urlParams.get("show");
      const showIdFromLocal = localStorage.getItem("CURRENT_SHOW_ID");
      const showId = showIdFromUrl || showIdFromLocal;

      // Always validate show via API if we have a show ID (no caching)
      if (showId && window.backend) {
        const { isValid, partner, show } = await validateShow(showId);
        console.log("VALIDATE SHOW", isValid, partner, show);

        if (isValid && partner?.siteName?.length) {
          // Store show context in localStorage for persistence across sessions
          localStorage.setItem("CURRENT_SHOW_ID", showId);
          localStorage.setItem("CURRENT_SHOW_SITE", partner?.siteName);
          if (show) {
            localStorage.setItem("CURRENT_SHOW_NAME", show?.name);
            localStorage.setItem("CURRENT_SHOW_HOSTESS_NAME", show?.hostessName);
          }
          // Mark that the PWS came from a show URL
          isFromShowUrl = true;
          return partner.siteName;
        }

        // Show is closed but partner data is available — use partner's PWS
        if (!isValid && partner?.siteName?.length) {
          // Remove stale show param from URL
          const currentUrlParams = new URLSearchParams(window.location.search);
          if (currentUrlParams.has("show")) {
            currentUrlParams.delete("show");
            const newQuery = currentUrlParams.toString();
            window.history.replaceState(
              {},
              "",
              window.location.pathname + (newQuery ? "?" + newQuery : "") + window.location.hash
            );
          }
          // Persist the stylist's PWS as the active site
          localStorage.setItem("CURRENT_SITE", partner.siteName);
          return partner.siteName;
        }
        // If show is invalid/closed with no partner data, attribution was already cleared
      }

      const paramPWS = urlParams.get("pws");
      if (paramPWS) return paramPWS;

      // Fallback: check referrer URL for pws param
      try {
        const referrerUrl = document.referrer || sessionStorage.getItem("TRACKING_REFERRER");
        if (referrerUrl) {
          const referrerParams = new URL(referrerUrl).searchParams;
          const referrerPWS = referrerParams.get("pws");
          if (referrerPWS) return referrerPWS;
        }
      } catch (e) {
        // referrer URL may be invalid or cross-origin, ignore
      }

      const localPWS = localStorage.getItem("CURRENT_SITE");
      if (localPWS) return localPWS;

      return null;
    };

    const getPreferredPWS = async () => {
      const sessionPWS = sessionStorage.getItem("PREFERRED_SITE");
      if (sessionPWS) return sessionPWS;

      const preferredStylistId = window?.customer?.preferredStylistId;
      if (preferredStylistId && window.backend) {
        const partner = await window.backend.getPartnerByStylistId(preferredStylistId);
        if (partner?.siteName?.length) {
          return partner.siteName;
        }
      }

      return null;
    };

    const loadTrackingInfo = () => {
      if (!sessionStorage.getItem("TRACKING_LANDING_URL")) {
        sessionStorage.setItem("TRACKING_LANDING_URL", window.location.href);
      }

      if (!sessionStorage.getItem("TRACKING_REFERRER") && document.referrer) {
        sessionStorage.setItem("TRACKING_REFERRER", document.referrer);
      }
    };

    function updateUrlPWS(pws) {
      const urlParams = new URLSearchParams(window.location.search);
      const showId = localStorage.getItem("CURRENT_SHOW_ID");
      urlParams.set("pws", pws);
      if (showId) {
        urlParams.set("show", showId);
      } else {
        // Remove show param if no valid show attribution exists
        urlParams.delete("show");
      }

      // Preserve the hash value if it exists
      const hashValue = window.location.hash;

      // Update URL while maintaining the hash
      window.history.replaceState({}, "", window.location.pathname + "?" + urlParams.toString() + hashValue);
    }

    async function handlePWSMismatch(currentPWS, preferredPWS) {
      if (!window.backend) {
        console.error('[Attribution] Cannot handle PWS mismatch - window.backend not available');
        return;
      }

      const [currentPartner, preferredPartner] = await Promise.all([
        window.backend.getPartnerBySite(currentPWS),
        window.backend.getPartnerBySite(preferredPWS),
      ]);

      if (!(currentPartner && preferredPartner)) {
        return;
      }
      document.dispatchEvent(
        new CustomEvent("theme:partner:mismatch", {
          detail: { current: currentPartner, preferred: preferredPartner },
        })
      );
    }

    async function handlePartnerResolution(currentPWS, preferredPWS) {
      if (!window.backend) {
        console.error('[Attribution] Cannot resolve partner - window.backend not available');
        return;
      }

      const currentOrPreferredPWS = currentPWS || preferredPWS;
      if (!currentOrPreferredPWS) {
        return;
      }

      const partner = await window.backend.getPartnerBySite(
        currentOrPreferredPWS
      );

      if (!partner?.siteName?.length) {
        return;
      }

      if (
        window?.customer &&
        window.customer?.customerType !== "Stylist" &&
        window.customer?.preferredStylistId === null
      ) {
        window.backend.syncPreferredStylist(partner.siteName);
        window.customer.preferredStylistId = partner?.stylistId;
      }

      updateUrlPWS(partner.siteName);

      localStorage.setItem("CURRENT_SITE", partner.siteName);
      localStorage.setItem("CURRENT_STYLIST_ID", partner.stylistId);
      sessionStorage.setItem("PREFERRED_SITE", partner.siteName);

      window.partner = partner;
      // Disable affinity update
      // document.dispatchEvent(
      //   new CustomEvent("theme:partner:update", { detail: { partner } })
      // );

      document.dispatchEvent(new CustomEvent("theme:partner:render", { detail: { partner } }));
    }

    document.addEventListener("DOMContentLoaded", async () => {
      // Defensive check: Ensure backend API is available
      if (!window.backend) {
        console.error('[Attribution] window.backend is not available. Backend.js may not be loaded yet.');
        return;
      }

      loadTrackingInfo();
      const currentPWS = await getCurrentPWS();
      const preferredPWS = await getPreferredPWS();
      
      if (window?.customer && window.customer?.isStylist) {
        updateUrlPWS(window.customer.siteName);
        localStorage.setItem("CURRENT_SITE", window.customer.siteName);
        localStorage.setItem("CURRENT_STYLIST_ID", window.customer?.stylistId ?? "NONE");
        await handlePartnerResolution(
          window.customer.siteName,
          window.customer.siteName
        );
      } else {
        const isDifferentPWS = currentPWS && preferredPWS && currentPWS !== preferredPWS;

        // If landing from a show URL, always use the show's stylist without showing the modal
        if (isDifferentPWS && !isFromShowUrl) {
          await handlePWSMismatch(currentPWS, preferredPWS);
        } else {
          await handlePartnerResolution(currentPWS, preferredPWS);
          //TODO: should be this triiger when the partner is null and the variable pws exists?
          if (window.partner !== null && window.partner?.status === "Former") {
            document.dispatchEvent(
              new CustomEvent("theme:partner:unavailable", {
                detail: { partner: window.partner },
              })
            );
          }
        }
      }
    });
  } catch (e) {
    console.error(e);
  }
})();
